import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  makeStyles,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip
} from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';

const useStyles = makeStyles((theme) => ({
  content: {
    padding: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  section: {
    marginBottom: theme.spacing(3),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  sectionTitle: {
    marginBottom: theme.spacing(1),
    fontWeight: 600,
    color: theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  list: {
    maxHeight: 200,
    overflow: 'auto',
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  listItem: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1, 2),
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  chip: {
    marginLeft: theme.spacing(1),
    fontSize: '0.75rem',
  },
  summaryTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    color: '#1976d2',
  },
  summaryCards: {
    display: 'flex',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  summaryCard: {
    flex: 1,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  cardTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
    color: theme.palette.text.primary,
    fontSize: '1rem',
  },
  cardItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  cardLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  cardValue: {
    fontWeight: 500,
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
  },
  cardSubItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(2),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  cardSubLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
  },
  cardSubValue: {
    fontWeight: 400,
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
  },
  listItemText: {
    '& .MuiListItemText-primary': {
      fontWeight: 500,
      color: theme.palette.text.primary,
    },
    '& .MuiListItemText-secondary': {
      color: theme.palette.text.secondary,
      fontSize: '0.875rem',
    },
  },
}));

const ImportReportModal = ({ 
  open, 
  onClose,
  skippedLines = [],
  failedLines = [],
  duplicateLines = [],
  totalContacts,
  importedContacts,
  duplicateContacts = 0,
  isCancelled = false
}) => {
  const classes = useStyles();

  // Para importações canceladas, "não importados" são os contatos restantes
  // Para importações completas, "não importados" são os que falharam/foram pulados
  const notImportedCount = isCancelled 
    ? totalContacts - importedContacts 
    : skippedLines.length + failedLines.length + duplicateLines.length;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h6">Relatório de Importação</Typography>
      </DialogTitle>
      
      <DialogContent className={classes.content}>
        {/* Seção de Resumo */}
        <Box className={classes.section}>
          <Typography variant="subtitle1" className={classes.summaryTitle}>
            Resumo
          </Typography>
          
          <Box className={classes.summaryCards}>
            {/* Card 1: Total e Importados */}
            <Box className={classes.summaryCard}>
              <Typography variant="subtitle2" className={classes.cardTitle}>
                Importados
              </Typography>
              <Box className={classes.cardItem}>
                <Typography className={classes.cardLabel}>Total na planilha:</Typography>
                <Typography className={classes.cardValue}>{totalContacts}</Typography>
              </Box>
              <Box className={classes.cardItem}>
                <Typography className={classes.cardLabel}>Importados com sucesso:</Typography>
                <Typography className={classes.cardValue}>{importedContacts}</Typography>
              </Box>
            </Box>

            {/* Card 2: Não Importados */}
            <Box className={classes.summaryCard}>
              <Typography variant="subtitle2" className={classes.cardTitle}>
                Não Importados
              </Typography>
              <Box className={classes.cardItem}>
                <Typography className={classes.cardLabel}>Total não importados:</Typography>
                <Typography className={classes.cardValue}>{notImportedCount}</Typography>
              </Box>
              <Box className={classes.cardSubItem}>
                <Typography className={classes.cardSubLabel}>Duplicados:</Typography>
                <Typography className={classes.cardSubValue}>{duplicateContacts}</Typography>
              </Box>
              <Box className={classes.cardSubItem}>
                <Typography className={classes.cardSubLabel}>Número vazio:</Typography>
                <Typography className={classes.cardSubValue}>{skippedLines.length}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Seção de Linhas com Número Vazio */}
        {skippedLines.length > 0 && (
          <Box className={classes.section}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              Linhas com Número Vazio ({skippedLines.length})
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Estas linhas foram ignoradas porque o campo de número estava vazio ou inválido.
            </Typography>
            <List dense className={classes.list}>
              {skippedLines.map(({line, number}, index) => (
                <ListItem key={index} className={classes.listItem}>
                  <ListItemText 
                    className={classes.listItemText}
                    primary={`Linha ${line} - ${number || 'N/A'}`}
                    secondary="Número de telefone vazio ou inválido"
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Seção de Linhas com Erro de Importação */}
        {failedLines.length > 0 && (
          <Box className={classes.section}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              Linhas com Erro de Importação ({failedLines.length})
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Estas linhas falharam durante a importação devido a erros no processamento.
            </Typography>
            <List dense className={classes.list}>
              {failedLines.map(({line, reason, number}, index) => (
                <ListItem key={index} className={classes.listItem}>
                  <ListItemText 
                    className={classes.listItemText}
                    primary={`Linha ${line} - ${number || 'N/A'}`}
                    secondary={reason}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Seção de Contatos Duplicados */}
        {duplicateLines.length > 0 && (
          <Box className={classes.section}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              Contatos Duplicados ({duplicateLines.length})
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Estes contatos já existiam na lista e não foram importados novamente.
            </Typography>
            <List dense className={classes.list}>
              {duplicateLines.map(({line, number}, index) => (
                <ListItem key={index} className={classes.listItem}>
                  <ListItemText 
                    className={classes.listItemText}
                    primary={`Linha ${line} - ${number || 'N/A'}`}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {isCancelled && (
          <Alert severity="info">
            <AlertTitle>Importação Cancelada</AlertTitle>
            A importação foi interrompida antes da conclusão. {importedContacts} de {totalContacts} contatos foram processados.
            {importedContacts > 0 && ' Os contatos já importados foram mantidos.'}
            {notImportedCount === 0 && (
              <div style={{ marginTop: '8px' }}>
                <strong>Nota:</strong> Como a importação foi cancelada antes da validação completa,
                alguns contatos podem não ter sido verificados quanto a problemas.
              </div>
            )}
          </Alert>
        )}
        
        {notImportedCount === 0 && !isCancelled && (
          <Alert severity="success">
            <AlertTitle>Importação Completa!</AlertTitle>
            Todos os {totalContacts} contatos foram importados com sucesso.
          </Alert>
        )}
        
        {notImportedCount > 0 && !isCancelled && (
          <Alert severity="warning">
            <AlertTitle>Importação com Problemas</AlertTitle>
            {importedContacts} de {totalContacts} contatos foram importados com sucesso. {notImportedCount} contatos não puderam ser importados.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary" variant="contained">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportReportModal;